import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import {
  EgoodReasonValues,
  EsafeStorageValues,
  ILicenceSafeStorage,
  ILicenceTemplateResponse,
} from 'models/licenceConfigurator';
import { Accordion } from 'components/molecules/Accordion';
import { FormSelect } from 'components/molecules/FormSelect';
import { Stack, Typography } from '@mui/material';
import { Table } from 'components/molecules/Table';
import { StyledFormElementWrapper } from 'theme/styles';
import { findInDictionary, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityCustomField,
  ILegalEntityData,
} from 'components/organisms/LegalEntityDetails';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate, formatOnlyDateToRequest } from 'utils/date';
import { LETypes } from 'constants/LegalEntityTypes';
import { FormNumberInput } from 'components/molecules/FormNumberInput';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import FormLabel from 'components/atoms/FormLabel';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import {
  IDocument,
  IExtendedLegalEntityLicenceDetails,
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceDetails,
  ILegalEntityLicenceVaryFormValues,
} from 'models/legalEntity';
import {
  ILicenceAttachment,
  IOwner,
  IOwnerIndividual,
  IOwnerOrganisation,
} from 'models/licence';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { yupResolver } from '@hookform/resolvers/yup';
import { useScroll } from 'hooks/useScroll';
import { useCustomFormError } from 'hooks/useCustomFormError';
import { mapBlankToNull } from 'utils/form';
import { sanitizeVaryFormData } from 'utils/storeDataTransforms';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { ScrollTarget } from 'components/atoms/ScrollTarget';
import { useAlert } from 'models/alertContext';
import { FormListWrapper } from 'components/atoms/FormListWrapper';
import { useLang } from 'models/langContext';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { Box } from '@mui/system';
import { ERouteLinks } from 'models/route';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { ILicenceFirearmsResponse } from 'models/licencesFirearms';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { EIconTypes } from 'constants/Icons';
import { Chips } from 'components/atoms/Chips';
import LicenceStore from 'state/LicenceStore';
import { EPermission } from 'models/permissions';
import { useAccount } from 'pages/AccountPage/hooks';
import { usePermission } from 'hooks/usePermission';
import { hasFirearmsToRemove } from 'utils/firearmValidation';
import { areObjectsEqual } from 'utils/objects';
import { formatAddress } from 'utils/legalAddress';
import { StyledLicenceWrapper } from './styles';
import {
  ELicenceStatus,
  ELicenceStatusIds,
  mapDocumentsToDataSource,
  prepareCustomFieldPayload,
} from '../helpers';
import { DocumentForm } from './documentForm';
import { useLegalEntities } from '../hooks';
import { useFormSchema } from './schema';
import {
  documentsTableColumns,
  mapAttachmentsToDataSource,
  mapLEExtendedAddressToSafeStorage,
  safeStorageTableColumns,
} from './helpers';
import {
  getDictionaryValue,
  getLicenceStatusChipVariant,
  mapWeaponToDataSource,
  weaponTableColumns,
} from '../LegalEntityViewLicencePage/helpers';
import { useVaryLicence } from './hooks';
import { WeaponTableCard } from '../LegalEntityViewLicencePage/WeaponTableCard';
import { LicenceFormOwnerDetails } from '../LegalEntityLicenceForm/LicenceFormOwnerDetails';
import { ESize } from 'models/galery';
import { getGalleryMain, getGalleryMainPreview } from 'requests/gallery';
import { renderPaymentStatusFieldColor } from 'pages/CaseManagementPage/CasesPage/helpers';
import { ICase } from 'models/cases';
import { ECustomFieldObjectType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ICustomFieldForm } from '../IndividualSearchForm';

interface ILicenceVaryForm {
  legalEntityLicenceDetails: ILegalEntityLicenceDetails;
  caseData: ICase;
  licence: ILicenceTemplateResponse;
  initialFormValues: ILegalEntityLicenceVaryFormValues;
  legalEntity: ILegalEntityData | IOrgLegalEntityData | null;
  type: LETypes;
  accessibleWeaponLicenceGoodReason: IDictionary[];
  primaryAddress: ILegalEntityExtendedAddress | undefined;
  availableAddresses: ILegalEntityExtendedAddress[];
  backLink: () => void;
  setUpdateScheduledModal: () => void;
  breadcrumbs: any;
  saveToStory: (formData: any) => void;
}

const LicenceVaryForm = ({
  legalEntityLicenceDetails,
  caseData,
  type,
  licence,
  initialFormValues,
  legalEntity,
  accessibleWeaponLicenceGoodReason,
  primaryAddress,
  availableAddresses,
  backLink,
  setUpdateScheduledModal,
  breadcrumbs,
  saveToStory,
}: ILicenceVaryForm) => {
  const { t } = useTranslations();
  const location = useLocation();
  const { pathname }: any = location;
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expand = searchParams.get('expand');
  const { shortDateFormat, currency, getProperties } = useGlobalProperty();
  const { selectedLanguage } = useLang();
  const { elRef, processFormErrors, preventSubmitIfError } = useScroll();
  const { id = '', licenceId = '' } = useParams<{
    id: string;
    licenceId: string;
  }>();
  const { account, getSelfAccountData } = useAccount();
  const { hasPermissions } = usePermission();
  const { setAlert, clearAlert, samePageAlert } = useAlert();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [firearmToLinkData, setFirearmToLinkData] = useState<ILicenceFirearmsResponse[]>([]);
  const [firearmLinkToBeRemovedIds, setFirearmLinkToBeRemovedIds] = useState<string[]>([]);
  const [licenceWeaponData, setLicenceWeaponData] = useState<ILicenceFirearmsResponse[]>([]);
  const [photoFile, setPhotoFile] = useState<any>(null);
  const [image, setImage] = useState<any>(null);
  const [safeStorages, setSafeStorages] = useState<ILicenceSafeStorage[]>([]);
  const [licenceAttachmentsData, setLicenceAttachmentsData] = useState<ILicenceAttachment[]>(
    [],
  );

  const [prefixForLE, setPrfixForLE] = useState<string>();
  const [saveButtonIsDisabled, setSaveButtonIsDisabled] = useState(false);

  const compareSafeStorage = (a: any[], b: any[]) => {
    const isSame =
      a.length === b.length &&
      a.every((x) => b.some((y) => y.addressUniqueId === x.addressUniqueId));

    return a?.length === b.length && !isSame;
  };

  const hasIllegalFirearms = useMemo(() => {
    if (!account) return false;

    const allFirearms = [...licenceWeaponData, ...firearmToLinkData];
    const licenceOwnerUniqueId = legalEntityLicenceDetails?.owner?.legalUniqueId;
    const hasViewAllLEPermission = hasPermissions(EPermission.VIEW_ALL_LE) === true;
    const hasViewAllFirearmsPermission =
      hasPermissions(EPermission.VIEW_ALL_FIREARMS) === true;

    return hasFirearmsToRemove(
      allFirearms,
      licenceOwnerUniqueId,
      account,
      hasViewAllFirearmsPermission,
      hasViewAllLEPermission,
    );
  }, [
    licenceWeaponData,
    firearmToLinkData,
    legalEntityLicenceDetails,
    account,
    hasPermissions,
  ]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const syncRemovedIdsFromStore = useCallback(() => {
    if (!licenceId) {
      setFirearmLinkToBeRemovedIds([]);
      return;
    }
    const ids = LicenceStore.getFormFirearmLinkToBeRemovedIds(licenceId);
    setFirearmLinkToBeRemovedIds(ids || []);
  }, [licenceId]);

  const loadDataFromStore = useCallback(() => {
    if (licenceId) {
      const licenceWeaponFromStore = LicenceStore.getFormFirearmCurrentLink(licenceId);
      if (licenceWeaponFromStore) {
        setLicenceWeaponData(licenceWeaponFromStore);
      }
      const firearmToLinkFromStore = LicenceStore.getFormWeapon(licenceId);
      setFirearmToLinkData(firearmToLinkFromStore);

      syncRemovedIdsFromStore();

      const safeStoragesFromStore = LicenceStore.getFormSafeStorages(licenceId);

      if (safeStoragesFromStore && safeStoragesFromStore.length > 0) {
        setSafeStorages(safeStoragesFromStore);
      } else if (availableAddresses && availableAddresses.length > 0) {
        const data = availableAddresses.map((item) => ({
          addressUniqueId: item.uniqueId,
          typeUniqueId: item.typeUniqueId,
          line1: item.line1,
          line2: item.line2,
          city: item.city,
          postalCode: item.postalCode,
          regionUniqueId: item.regionUniqueId,
          countryUniqueId: item.countryUniqueId,
          latitude: item.latitude,
          longitude: item.longitude,
          storage: item.storage,
          isDefault: item.isDefault,
        }));
        setSafeStorages(data);
      }

      const licenceAttachmentsDataFromStore =
        LicenceStore.getFormLicenceAttachmentsData(licenceId);
      if (licenceAttachmentsDataFromStore) {
        setLicenceAttachmentsData(licenceAttachmentsDataFromStore);
      }

      const photoFileFromStore = LicenceStore.getFormPhotoFile(licenceId);
      if (photoFileFromStore) {
        setPhotoFile(photoFileFromStore);
      }

      const imageFromStore = LicenceStore.getFormImage(licenceId);
      if (imageFromStore) {
        setImage(imageFromStore);
      }
    }
  }, [licenceId, initialFormValues.conditions?.safeStorages, syncRemovedIdsFromStore]);

  useEffect(() => {
    loadDataFromStore();
  }, [loadDataFromStore, licenceId, initialFormValues.conditions?.safeStorages]);

  useEffect(() => {
    loadDataFromStore();
  }, [loadDataFromStore, pathname]);

  useEffect(() => {
    syncRemovedIdsFromStore();
  }, [syncRemovedIdsFromStore, licenceWeaponData.length, firearmToLinkData.length]);

  useEffect(() => {
    LicenceStore.setFormSafeStorages(safeStorages, licenceId);
  }, [safeStorages]);

  useEffect(() => {
    LicenceStore.setFormPhotoFile(photoFile, licenceId);
  }, [photoFile]);

  useEffect(() => {
    LicenceStore.setFormImage(image, licenceId);
  }, [image]);

  const licenceDetails = legalEntityLicenceDetails as IExtendedLegalEntityLicenceDetails;

  const {
    handleVaryLicence,
    getLicenceAttachmentFile,
    getScheduledLicenceData,
    licenceMismatchingLegalEntity,
    getLicenceMismatchingLegalEntityData,
    getCustomField,
    customFieldsLicence,
  } = useLegalEntities();

  const {
    loadDictionaries,
    weaponLicenceStatus,
    weaponLicencePaymentStatus,
    weaponLicencingAuthority,
    region,
    country,
    weaponLicenceCompetency,
    weaponLicenceCategory,
    weaponLicenceCondition,
    attachmentDocumentTypeData,
    addressType,
    civilianPrefix,
    state,
    firearmType,
    make,
    model,
    caliber,
    firearmAction,
  } = useVaryLicence();

  useEffect(() => {
    (async function init() {
      getProperties();
      await getSelfAccountData();
      await loadDictionaries([
        EDictionaryTypes.applicationProcessTypes,
        EDictionaryTypes.weaponLicenceStatus,
        EDictionaryTypes.weaponLicenceCategory,
        EDictionaryTypes.weaponLicenceCompetency,
        EDictionaryTypes.weaponLicenceGoodReason,
        EDictionaryTypes.weaponLicencePaymentStatus,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.weaponLicenceRegion,
        EDictionaryTypes.Country,
        EDictionaryTypes.AddressType,
        EDictionaryTypes.weaponLicenceCondition,
        EDictionaryTypes.attachmentDocumentTypeData,
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.State,
        EDictionaryTypes.FirearmType,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Caliber,
        EDictionaryTypes.FirearmAction,
      ]);
      await getCustomField(ECustomFieldObjectType.LICENCE);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (photoFile) {
      setImage(URL.createObjectURL(photoFile));
    } else {
      setImage(null);
    }
  }, [photoFile]);

  useEffect(() => {
    if (legalEntity && type === LETypes.individual && !image) {
      (async function getLegalEntityPhotoInit() {
        const photo = await getGalleryMainPreview(legalEntity.uniqueId, ESize.THUMBNAIL);
        const photoData = await getGalleryMain(legalEntity.uniqueId);
        if (photo && photoData) {
          setImage(photo);
          setValue('galleryEntryUniqueId', photoData.uniqueId);
        } else {
          setValue('galleryEntryUniqueId', null);
        }
      })();
    }
  }, [legalEntity, type]);

  useEffect(() => {
    const isMissingPrimaryAddress = !primaryAddress;
    const hasNoAvailableAddresses = !availableAddresses.length;

    if (hasNoAvailableAddresses || isMissingPrimaryAddress) {
      setSaveButtonIsDisabled(true);
      return;
    }

    if (type === LETypes.individual) {
      const missingIdentityDocs =
        !legalEntity?.identityDocumentNumber || !legalEntity?.identityDocumentTypeUniqueId;
      const requiredIdentityDocs = licence.owner.individual.documentTypeAndNumber;

      if (missingIdentityDocs && requiredIdentityDocs) {
        setSaveButtonIsDisabled(true);
      } else {
        setSaveButtonIsDisabled(false);
      }
    } else if (type === LETypes.organization) {
      if (licence.owner.organisation.departmentName && !legalEntity?.departmentName) {
        setSaveButtonIsDisabled(true);
      } else {
        setSaveButtonIsDisabled(false);
      }
    }
  }, [
    type,
    legalEntity,
    licence.owner.individual.documentTypeAndNumber,
    primaryAddress,
    availableAddresses.length,
    legalEntity?.departmentName,
    licence.owner.organisation.departmentName,
  ]);

  const getCurrentWeaponCount = () => licenceWeaponData.length + firearmToLinkData.length;
  const { schema } = useFormSchema(
    licence,
    customFieldsLicence,
    getCurrentWeaponCount,
    initialFormValues?.customFields as ILegalEntityCustomField[],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ILegalEntityLicenceVaryFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ILegalEntityLicenceVaryFormValues>,
  });

  const watchHasSafeStorage = watch('conditions.hasSafeStorage');
  const watchWeaponNumberLimit = watch('conditions.weaponNumberLimit');

  useCustomFormError({
    errorKey: 'firearm',
    shouldShowError: hasIllegalFirearms,
    errorMessage: t('legalEntity.licenceDetails.weapon.illegalFirearm.error'),
    setError,
    clearErrors,
  });

  useEffect(() => {
    processFormErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    setValue('conditions.safeStorages', safeStorages);
  }, [safeStorages]);

  useEffect(() => {
    if (civilianPrefix?.length && legalEntity) {
      const prefix = civilianPrefix?.find(
        (item: IDictionary) => item?.uniqueId === legalEntity?.prefixValueUniqueId,
      )?.name;
      setPrfixForLE(prefix);
    } else {
      setPrfixForLE('');
    }
  }, [civilianPrefix, legalEntity]);

  useEffect(() => {
    getLicenceMismatchingLegalEntityData(licenceId);
  }, [licenceId]);

  const actionback = (showModal: boolean) => {
    if (showModal) {
      setUpdateScheduledModal();
    } else {
      backLink();
    }
  };

  const onSubmit = async (data: ILegalEntityLicenceVaryFormValues) => {
    if (preventSubmitIfError(hasIllegalFirearms, errors, 'firearm')) {
      return undefined;
    }

    setIsSubmitLoading(true);

    delete data.suitabilityAssessmentPassed;

    data = sanitizeVaryFormData(data);

    let individual: IOwnerIndividual | undefined;
    let organisation: IOwnerOrganisation | undefined;
    if (type === LETypes.individual) {
      individual = {
        firstName: legalEntity?.firstName,
        lastName: legalEntity?.lastName,
        middleNames: legalEntity?.middleNames,
        genderUniqueId: legalEntity?.genderUniqueId,
        dateOfBirth: licence.owner.individual.dateOfBirth
          ? formatOnlyDateToRequest(legalEntity?.dateOfBirth)
          : null,
        placeOfBirth: licence.owner.individual.placeOfBirth ? legalEntity?.placeOfBirth : null,
        nationalityUniqueId: licence.owner.individual.nationality
          ? legalEntity?.nationalityUniqueId
          : null,
        height: licence.owner.individual.height ? legalEntity?.height : null,
        occupation: licence.owner.individual.occupation ? legalEntity?.occupation : null,
        secondSurname: licence.owner.individual.secondSurname
          ? legalEntity?.secondSurname
          : null,
        identityDocumentTypeUniqueId: licence.owner.individual.documentTypeAndNumber
          ? mapBlankToNull(legalEntity?.identityDocumentTypeUniqueId)
          : null,
        identityDocumentNumber: licence.owner.individual.documentTypeAndNumber
          ? mapBlankToNull(legalEntity?.identityDocumentNumber)
          : null,
        prefixValueUniqueId: legalEntity?.prefixValueUniqueId,
      };
    } else {
      organisation = {
        organisationName: legalEntity?.organizationName,
        departmentName: licence.owner.organisation.departmentName
          ? legalEntity?.departmentName
          : null,
        organisationCategoryUniqueId: legalEntity?.organisationCategoryUniqueId,
        organisationTypeUniqueIds: licence.owner.organisation.organisationType
          ? legalEntity?.organisationTypeUniqueIds
          : null,
      };
    }

    const owner: IOwner | null = legalEntity
      ? {
          legalUniqueId: legalEntity.uniqueId,
          individual,
          organisation,
          primaryAddress: primaryAddress
            ? {
                typeUniqueId: primaryAddress?.typeUniqueId,
                line1: primaryAddress?.line1,
                line2: primaryAddress?.line2,
                city: primaryAddress?.city,
                postalCode: primaryAddress?.postalCode,
                regionUniqueId: primaryAddress?.regionUniqueId,
                countryUniqueId: primaryAddress?.countryUniqueId,
                latitude: primaryAddress?.latitude,
                longitude: primaryAddress?.longitude,
                storage: primaryAddress?.storage,
                addressUniqueId: primaryAddress?.uniqueId,
              }
            : null,
          displayUniqueId: legalEntity.displayUniqueId,
        }
      : null;

    if (!owner) return undefined;

    data.conditions.areaOfValidityUniqueId = mapBlankToNull(
      data.conditions.areaOfValidityUniqueId,
    );

    if (licence.conditions.specificCountryArea) {
      delete data.conditions.areaOfValidityUniqueId;
    }

    data.conditions.competencyUniqueId = mapBlankToNull(data.conditions.competencyUniqueId);
    data.conditions.weaponCategorisationUniqueId = mapBlankToNull(
      data.conditions.weaponCategorisationUniqueId,
    );
    data.conditions.carry = mapBlankToNull(data.conditions.carry);
    data.conditions.additionalConditions = mapBlankToNull(
      data.conditions.additionalConditions,
    );
    data.conditions.safeStorageDescription = mapBlankToNull(
      data.conditions.safeStorageDescription,
    );

    if (watchHasSafeStorage?.toString() === 'true') {
      const dataSafeStorages = safeStorages.map((address) => {
        const item = availableAddresses.find((i) => i.uniqueId === address.addressUniqueId);
        return {
          addressUniqueId: item?.uniqueId || address.addressUniqueId,
          typeUniqueId: item?.typeUniqueId || address.typeUniqueId,
          line1: item?.line1 || address.line1,
          line2: item?.line2 || address.line2,
          city: item?.city || address.city,
          postalCode: item?.postalCode || address.postalCode,
          regionUniqueId: item?.regionUniqueId || address.regionUniqueId,
          countryUniqueId: item?.countryUniqueId || address.countryUniqueId,
          latitude: item?.latitude || address.latitude,
          longitude: item?.longitude || address.longitude,
          storage: address.storage,
          isDefault: item?.isDefault || address.isDefault,
        };
      });
      data.conditions.safeStorages = dataSafeStorages;
      data.conditions.hasSafeStorage = true;
    } else {
      data.conditions.safeStorages = [];
      data.conditions.hasSafeStorage = false;
    }

    const removalIds =
      LicenceStore.getFormFirearmLinkToBeRemovedIds(licenceId) || firearmLinkToBeRemovedIds;
    data.firearm.firearmLinkToBeRemovedUniqueIds = removalIds;

    const newlyLinkedWeapons = firearmToLinkData;
    const reLinkedExistingWeapons = LicenceStore.getFormFirearmCurrentLink(licenceId) || [];
    const allWeaponsToLink = [...newlyLinkedWeapons, ...reLinkedExistingWeapons];

    const licenceWeaponDataIds = licenceWeaponData.map((item) => item.uniqueId);
    const weaponsToLink = allWeaponsToLink.filter(
      (item) => !licenceWeaponDataIds.includes(item.uniqueId),
    );

    data.firearm.firearmToLinkUniqueIds = weaponsToLink.map((item) => item.uniqueId);

    let updateScheduled: boolean | null = true;
    if (
      initialFormValues.conditions.additionalConditions !==
        data.conditions.additionalConditions ||
      initialFormValues.conditions.areaOfValidityUniqueId !==
        data.conditions.areaOfValidityUniqueId ||
      initialFormValues.conditions.carry !== data.conditions.carry ||
      initialFormValues.conditions.competencyUniqueId !== data.conditions.competencyUniqueId ||
      initialFormValues.conditions.goodReason !== data.conditions.goodReason ||
      !areObjectsEqual(
        initialFormValues.conditions.goodReasonUniqueIds,
        data.conditions.goodReasonUniqueIds,
      ) ||
      !!initialFormValues.conditions.hasSafeStorage !== !!data.conditions.hasSafeStorage ||
      initialFormValues.conditions.safeStorageDescription !==
        data.conditions.safeStorageDescription ||
      initialFormValues.conditions.weaponCategorisationUniqueId !==
        data.conditions.weaponCategorisationUniqueId ||
      initialFormValues.conditions.weaponNumberLimit !== data.conditions.weaponNumberLimit ||
      initialFormValues.conditions.safeStorages?.length !==
        data.conditions.safeStorages.length ||
      compareSafeStorage(
        initialFormValues.conditions.safeStorages,
        data.conditions.safeStorages ?? [],
      )
    ) {
      updateScheduled = false;
    }
    const limitSchedule = await getScheduledLicenceData(
      licenceDetails?.general?.licenceNumber ?? '',
    );

    const existsScheduleLicence = !!limitSchedule;

    if (limitSchedule && limitSchedule?.weaponNumberLimit) {
      if (
        limitSchedule.actualWeaponAmount +
          firearmToLinkData.length -
          firearmLinkToBeRemovedIds.length >
        limitSchedule?.weaponNumberLimit
      ) {
        updateScheduled = false;
      }
    }

    data.customFields = [];

    const customFieldPayload: ICustomFieldForm[] = prepareCustomFieldPayload(
      data,
      customFieldsLicence,
    );

    if (customFieldPayload?.length && customFieldPayload.length > 0) {
      data.customFields = customFieldPayload;
    } else {
      delete data.customFields;
    }

    try {
      await handleVaryLicence(
        data,
        updateScheduled === true && existsScheduleLicence,
        existsScheduleLicence,
        owner,
        () => actionback(updateScheduled === false && existsScheduleLicence),
        licenceId,
      );
    } finally {
      setIsSubmitLoading(false);
    }
    return undefined;
  };

  const handleHasSafeStorage = (e: any) => {
    if (e?.value?.toString() === 'true') {
      setValue('conditions.safeStorageDescription', '');
    } else if (e?.value?.toString() === 'false') {
      setSafeStorages([]);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'document.documents',
  });

  const documentSetToForm = async (document: IDocument) => {
    if (fields.length < 10) {
      append(document);
    } else {
      await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('addAttachment.maxFiles.text'),
        variant: EAlertVariants.error,
      });
    }
  };

  const handleDownload = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAttachmentDownload = async (attachmentId: string, name: string) => {
    try {
      const url = await getLicenceAttachmentFile(licenceId, attachmentId, 'download');

      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Błąd otwierania pliku', err);
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    remove(documentId);
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    const documentToRemoveUniqueIds = [
      ...getValues('document.documentToRemoveUniqueIds'),
      attachmentId,
    ];
    const filteredData = licenceAttachmentsData.filter(
      (item: ILicenceAttachment) => item.uniqueId !== attachmentId,
    );
    setLicenceAttachmentsData(filteredData);
    LicenceStore.setFormLicenceAttachmentsData(filteredData);

    LicenceStore.setFormLicenceAttachmentsToDeleteIds(documentToRemoveUniqueIds);
    setValue('document.documentToRemoveUniqueIds', documentToRemoveUniqueIds);
  };

  const handleDeleteWeapon = (firearmId: string, weaponId: string) => {
    if (licenceWeaponData.find((item) => item.uniqueId === weaponId)) {
      setLicenceWeaponData(licenceWeaponData.filter((item) => item.uniqueId !== weaponId));
      LicenceStore.setDeleteFromCurrentWeapon(weaponId, licenceId);

      const newRemovedIds = [...firearmLinkToBeRemovedIds, firearmId];
      setFirearmLinkToBeRemovedIds(newRemovedIds);
      LicenceStore.setFormFirearmLinkToBeRemovedIds(newRemovedIds, licenceId);
      syncRemovedIdsFromStore();
    }
    if (firearmToLinkData.find((item) => item.uniqueId === weaponId)) {
      const updatedLinkedWeapons = firearmToLinkData.filter(
        (item) => item.uniqueId !== weaponId,
      );
      setFirearmToLinkData(updatedLinkedWeapons);

      LicenceStore.replaceFormWeapon(updatedLinkedWeapons, licenceId);
      syncRemovedIdsFromStore();
    }
  };

  const handleLinkWeapon = () => {
    const values = getValues();
    saveToStory(values);

    navigate(
      `${ERouteLinks.LegalEntityLicenceLinkWeaponVaryRenew.replace(
        ':id',
        legalEntityLicenceDetails?.owner?.legalUniqueId as string,
      ).replace(':licenceId', licenceId as string)}?type=${
        type === LETypes.individual
          ? LEAccordionNames.INDIVIDUAL
          : LEAccordionNames.ORGANISATION
      }`,
      {
        state: {
          saveToStory: true,
          countCurrent: licenceWeaponData.length + firearmToLinkData.length,
          weaponNumberLimit: watchWeaponNumberLimit,
          removedExistingWeaponIds: firearmLinkToBeRemovedIds,
          prevLink: `${pathname}?type=${
            type === LETypes.individual
              ? LEAccordionNames.INDIVIDUAL
              : LEAccordionNames.ORGANISATION
          }&expand=${expand}`,
          breadcrumbs: [
            ...breadcrumbs,
            {
              label: t('legalEntity.licenceDetails.varyLicence.header'),
              route: pathname,
            },
          ],
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div ref={elRef}>
        <Accordion
          title={t('legalEntity.licence.generalInformation.label')}
          name="generalInformation"
          key="generalInformation"
          expanded
          alwaysExpanded
          hideExpandIcon
        >
          <StyledLicenceWrapper container size={{ xs: 12 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.licenceName')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licenceDetails?.general?.name)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.licenceCode')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licenceDetails?.general?.code)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.licenceNumber')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licenceDetails?.general?.licenceNumber)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.licenceStatus')}
              </Typography>
              <Typography variant="body2">
                <Chips
                  text={getDictionaryValue(
                    licenceDetails?.general?.statusUniqueId,
                    weaponLicenceStatus,
                  )}
                  variant={getLicenceStatusChipVariant(
                    licenceDetails?.general?.statusUniqueId ?? '',
                  )}
                  fullWidth
                />
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 / 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.startDate')}
              </Typography>
              <Typography variant="body2">
                {licenceDetails?.general?.startDate
                  ? formatDate(licenceDetails.general.startDate, shortDateFormat)
                  : emptyFieldHolder('')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 / 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licenceDetails.generalInformation.expiryDate')}
              </Typography>
              <Typography variant="body2">
                {licenceDetails?.general?.expiryDate
                  ? formatDate(licenceDetails.general.expiryDate, shortDateFormat)
                  : emptyFieldHolder('')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('caseManagement.cases.licenceAuthority.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(
                  getDictionaryValue(
                    licenceDetails?.general?.licenceAuthorityUniqueId,
                    weaponLicencingAuthority,
                  ),
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('caseManagement.payment.paymentStatus.label')}
                </Typography>
                <Typography variant="body2">
                  <Chips
                    text={findInDictionary(
                      caseData.paymentStatusUniqueId,
                      weaponLicencePaymentStatus,
                    )}
                    variant={renderPaymentStatusFieldColor(caseData)}
                    fullWidth
                  />
                </Typography>
              </Stack>
            </Grid>

            {(() => {
              const currentStatus = getDictionaryValue(
                licenceDetails?.general?.statusUniqueId,
                weaponLicenceStatus,
              )?.toUpperCase();

              if (
                currentStatus === ELicenceStatus.REVOKED ||
                currentStatus === ELicenceStatus.SUSPENDED ||
                currentStatus === ELicenceStatus.SURRENDERED
              ) {
                let reasonLabel = '';
                if (currentStatus === ELicenceStatus.REVOKED) {
                  reasonLabel = t(
                    'legalEntity.licenceDetails.generalInformation.reasonOfRevocation',
                  );
                } else if (currentStatus === ELicenceStatus.SUSPENDED) {
                  reasonLabel = t(
                    'legalEntity.licenceDetails.generalInformation.reasonOfSuspension',
                  );
                } else {
                  reasonLabel = t(
                    'legalEntity.licenceDetails.generalInformation.reasonOfSurrender',
                  );
                }

                return (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {reasonLabel}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(licenceDetails?.lastStatusChangeDescription)}
                    </Typography>
                  </Grid>
                );
              }
              return null;
            })()}
          </StyledLicenceWrapper>
        </Accordion>
        <Grid container size={{ xs: 12 }} columnSpacing="20px">
          <Grid size={{ xs: 12 }}>
            <FormCheckbox
              name="suitabilityAssessmentPassed"
              label={`${t('legalEntity.licence.suitabilityAssessmentPassed.label')}* `}
              control={control}
              labelWeight={600}
              errors={errors}
            />
          </Grid>
        </Grid>
        <Accordion
          title={t('legalEntity.licence.licenceOwner.label')}
          name="licenceOwner"
          key="licenceOwner"
          expanded
          alwaysExpanded
          hideExpandIcon
        >
          {!primaryAddress && !formatAddress(primaryAddress, country) && (
            <Grid sx={{ mb: 2 }}>
              <Alert
                text={t('legalEntity.licence.primaryAddressNotDefined.message')}
                variant={EAlertVariants.warning}
              />
            </Grid>
          )}
          {type === LETypes.individual &&
            licence.owner.individual.documentTypeAndNumber &&
            (!legalEntity?.identityDocumentNumber ||
              !legalEntity?.identityDocumentTypeUniqueId) && (
              <Grid sx={{ mb: 2 }}>
                <Alert
                  text={t('legalEntity.licence.identityDocumentNotDefined.message')}
                  variant={EAlertVariants.warning}
                />
              </Grid>
            )}
          {licence.owner.organisation.departmentName &&
            type === LETypes.organization &&
            !legalEntity?.departmentName && (
              <Grid sx={{ mb: 2 }}>
                <Alert
                  text={t('legalEntity.licence.departmentNameNotDefined.message')}
                  variant={EAlertVariants.warning}
                />
              </Grid>
            )}

          {legalEntityLicenceDetails.hasAnyLeDataChanged &&
            legalEntityLicenceDetails?.general?.statusUniqueId ===
              ELicenceStatusIds.ACTIVE && (
              <Grid sx={{ mb: 2.5 }}>
                <Alert
                  text={t('legalEntity.licenceDetails.updatedLegalEntityDataLoaded.text')}
                  variant={EAlertVariants.success}
                />
              </Grid>
            )}
          <LicenceFormOwnerDetails
            legalEntity={legalEntity}
            primaryAddress={primaryAddress}
            prefixForLE={prefixForLE}
            image={image}
            owner={licence.owner}
            licenceMismatchingLegalEntity={licenceMismatchingLegalEntity}
            type={type as LETypes}
          />
        </Accordion>
        <Accordion
          title={t('legalEntity.licence.licenceConditions.label')}
          name="licenceConditions"
          key="licenceConditions"
          alwaysExpanded
          hideExpandIcon
        />
        <Grid container size={{ xs: 12 }} columnSpacing="20px">
          {licence.conditions.competency && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(weaponLicenceCompetency)}
                setValue={setValue}
                name="conditions.competencyUniqueId"
                label={`${t('legalEntity.licence.competency.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          {licence.conditions.carryConditions && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(weaponLicenceCondition)}
                setValue={setValue}
                name="conditions.carry"
                label={`${t('legalEntity.licence.carryConditions.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          {licence.conditions.specificCountryArea ? (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={[{ label: t('licence.areaRestriction.national'), value: '1' }]}
                setValue={setValue}
                canBeEmpty={false}
                name="conditions.areaOfValidityUniqueId"
                label={t('legalEntity.licence.areaOfValidity.label')}
                control={control}
                errors={errors}
                required
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(region)}
                setValue={setValue}
                name="conditions.areaOfValidityUniqueId"
                label={`${t('legalEntity.licence.areaOfValidity.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          {licence.conditions.weaponCategory && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(weaponLicenceCategory)}
                setValue={setValue}
                name="conditions.weaponCategorisationUniqueId"
                label={`${t('legalEntity.licence.weaponCategorisation.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          {licence.conditions.weaponNumberLimit && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FormNumberInput
                name="conditions.weaponNumberLimit"
                label={`${t('legalEntity.licence.weaponNumberLimit.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          )}
          {licence.conditions.additionalConditions && (
            <Grid size={{ xs: 12 }}>
              <FormWyswigEditor
                name="conditions.additionalConditions"
                label={t('legalEntity.licence.additionalConditions.label')}
                setValue={setValue}
                control={control}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                charsLimit={1000}
                withValidation
                required
              />
            </Grid>
          )}
          <Grid container size={{ xs: 12 }}>
            {licence.conditions.goodReasonDictionaryIds.length > 0 && (
              <Grid sx={{ mb: 0.5 }}>
                <FormLabel
                  name="conditions.goodReasonUniqueIds"
                  label={t('legalEntity.licence.goodReason.label')}
                  required
                />
              </Grid>
            )}
            <FormListWrapper
              isError={!!errors.conditions?.goodReasonUniqueIds?.message}
              errorMessage={errors.conditions?.goodReasonUniqueIds?.message?.toString()}
            >
              <Grid container size={{ xs: 12 }}>
                {accessibleWeaponLicenceGoodReason &&
                  accessibleWeaponLicenceGoodReason.map((item: IDictionary) => (
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <FormCheckbox
                        name={`conditions.goodReasonUniqueIds.${item.uniqueId}`}
                        label={item.name}
                        control={control}
                        errors={errors}
                        withValidation={false}
                        booleanType
                      />
                    </Grid>
                  ))}
              </Grid>
            </FormListWrapper>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          {licence.conditions.goodReason === EgoodReasonValues.Text && (
            <FormInput
              name="conditions.goodReason"
              type={EFormInputTypes.textarea}
              minRows={3}
              multiline
              charsLimit={1000}
              showCounter
              label={t('legalEntity.licence.goodReason.single.label')}
              control={control}
              errors={errors}
              setValue={setValue}
              withValidation
              required
            />
          )}
        </Grid>
        {licence.conditions.safeStorage && (
          <>
            <Accordion
              title={t('legalEntity.licence.licenceConditions.safeStorage')}
              name="licenceConditions"
              key="licenceConditions"
              alwaysExpanded
              hideExpandIcon
            />
            {licence.conditions.safeStorage === EsafeStorageValues.Text && (
              <>
                <Grid size={{ xs: 12 }}>
                  <FormRadioGroup
                    name="conditions.hasSafeStorage"
                    label={t('legalEntity.licence.licenceConditions.hasSafeStorage.label')}
                    options={[
                      {
                        label: t(
                          'legalEntity.licence.licenceConditions.hasSafeStorage.yes.label',
                        ),
                        value: 'true',
                      },
                      {
                        label: t(
                          'legalEntity.licence.licenceConditions.hasSafeStorage.no.label',
                        ),
                        value: 'false',
                      },
                    ]}
                    control={control}
                    errors={errors}
                    booleanType
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormWyswigEditor
                    name="conditions.safeStorageDescription"
                    label={t('legalEntity.licence.safeStorageDescription.label')}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    charsLimit={1000}
                    withValidation
                    required
                    showCounter
                  />
                </Grid>
              </>
            )}
            {licence.conditions.safeStorage === EsafeStorageValues.Object && (
              <Grid size={{ xs: 12 }}>
                <Grid sx={{ mb: 2 }}>
                  <FormRadioGroup
                    name="conditions.hasSafeStorage"
                    label={t('legalEntity.licence.licenceConditions.hasSafeStorage.label')}
                    options={[
                      {
                        label: t(
                          'legalEntity.licence.licenceConditions.hasSafeStorage.yes.label',
                        ),
                        value: 'true',
                      },
                      {
                        label: t(
                          'legalEntity.licence.licenceConditions.hasSafeStorage.no.label',
                        ),
                        value: 'false',
                      },
                    ]}
                    control={control}
                    errors={errors}
                    booleanType
                    onChangeOption={(e) => handleHasSafeStorage(e)}
                  />
                </Grid>
                {watchHasSafeStorage !== '' && (
                  <div>
                    {watchHasSafeStorage?.toString() === 'true' ? (
                      <StyledFormElementWrapper>
                        {(() => {
                          const storageAddresses = availableAddresses.filter(
                            (address) => address?.storage,
                          );

                          const dataSource = mapLEExtendedAddressToSafeStorage(
                            availableAddresses,
                            region,
                            country,
                            addressType,
                            id as string,
                          );

                          return storageAddresses.length ? (
                            <>
                              <Table
                                columns={safeStorageTableColumns({
                                  selectedStorage: safeStorages,
                                  setSelectedStorage: setSafeStorages,
                                  t,
                                })}
                                dataSource={dataSource}
                              />
                              {errors.conditions?.safeStorages && (
                                <FormErrorLabel
                                  label={
                                    (errors.conditions?.safeStorages as { message?: string })
                                      ?.message ?? ''
                                  }
                                />
                              )}
                            </>
                          ) : (
                            <Alert
                              text={t(
                                'legalEntity.licence.storageAddressIsNotDefined.message',
                              )}
                              variant={EAlertVariants.error}
                            />
                          );
                        })()}
                      </StyledFormElementWrapper>
                    ) : (
                      <Grid size={{ xs: 12 }}>
                        <FormWyswigEditor
                          name="conditions.safeStorageDescription"
                          label={t('legalEntity.licence.safeStorageDescription.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          setError={setError}
                          clearErrors={clearErrors}
                          charsLimit={1000}
                          withValidation
                          required
                          showCounter
                        />
                      </Grid>
                    )}
                  </div>
                )}
              </Grid>
            )}
          </>
        )}
        <ScrollTarget id="firearm" />
        <Accordion
          title={t('legalEntity.licenceDetails.weapon.header')}
          subtitle={`${t('legalEntity.licenceDetails.weapon.limitNumber.text')} 
                      ${
                        watchWeaponNumberLimit ||
                        legalEntityLicenceDetails?.conditions?.weaponNumberLimit
                          ? `${licenceWeaponData.length + firearmToLinkData.length} / ${
                              watchWeaponNumberLimit ||
                              legalEntityLicenceDetails?.conditions?.weaponNumberLimit
                            }`
                          : t('legalEntity.licenceDetails.weapon.unlimited.text')
                      } `}
          name="link-weapon"
          key="link-weapon"
          alwaysExpanded
          hideExpandIcon
        />

        {hasIllegalFirearms && (
          <Grid sx={{ mb: 2 }}>
            <Alert
              text={t('legalEntity.licenceDetails.weapon.illegalFirearm.error')}
              variant={EAlertVariants.error}
            />
          </Grid>
        )}

        {legalEntityLicenceDetails &&
          legalEntityLicenceDetails.general.statusUniqueId === ELicenceStatusIds.ACTIVE && (
            <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'right', mb: 2 }}>
              <Button
                id="link-weapon"
                variant={EButtonVariants.contained}
                icon={EIconTypes.plus}
                label={t('legalEntity.licenceDetails.linkWeapon.label')}
                onClick={handleLinkWeapon}
              />
            </Grid>
          )}

        <Grid container>
          {isDesktop ? (
            <Table
              columns={weaponTableColumns({
                propsLink: `?type=${
                  type === LETypes.individual
                    ? LEAccordionNames.INDIVIDUAL
                    : LEAccordionNames.ORGANISATION
                }`,
                handleDelete: (firearmId: string, weaponId: string) =>
                  handleDeleteWeapon(firearmId, weaponId),
                leId: legalEntityLicenceDetails?.owner?.legalUniqueId as string,
                licenceId: licenceId as string,
                state: {
                  breadcrumbs,
                },
                account,
                hasViewAllFirearmsPermission:
                  hasPermissions(EPermission.VIEW_ALL_FIREARMS) === true,
                hasViewAllLEPermission: hasPermissions(EPermission.VIEW_ALL_LE) === true,
                licenceOwnerUniqueId: legalEntityLicenceDetails?.owner?.legalUniqueId,
                showInfoColumn: hasIllegalFirearms,
                t,
              })}
              dataSource={mapWeaponToDataSource(
                [...licenceWeaponData, ...firearmToLinkData],
                state,
                firearmType,
                make,
                model,
                caliber,
                firearmAction,
              )}
            />
          ) : (
            <CardList
              items={mapWeaponToDataSource(
                [...licenceWeaponData, ...firearmToLinkData],
                state,
                firearmType,
                make,
                model,
                caliber,
                firearmAction,
              )}
              render={(data, index) => (
                <WeaponTableCard
                  data={data}
                  propsLink={`?type=${
                    type === LETypes.individual
                      ? LEAccordionNames.INDIVIDUAL
                      : LEAccordionNames.ORGANISATION
                  }`}
                  leId={legalEntityLicenceDetails?.owner?.legalUniqueId as string}
                  licenceId={licenceId as string}
                  account={account}
                  hasViewAllFirearmsPermission={
                    hasPermissions(EPermission.VIEW_ALL_FIREARMS) === true
                  }
                  key={index}
                />
              )}
            />
          )}
        </Grid>

        <Accordion
          title={t('legalEntity.licence.documents.label')}
          name="documents"
          key="documents"
          alwaysExpanded
          hideExpandIcon
        />
        <DocumentForm setToForm={documentSetToForm} />
        <Table
          columns={documentsTableColumns({
            handleDownload: (file) => {
              handleDownload(file);
            },
            handleDownloadAttachment: (attachmentId: string, name: string) =>
              handleAttachmentDownload(attachmentId, name),
            handleDelete: (documentId: number) => handleDeleteDocument(documentId),
            handleDeleteAttachment: (attachmentId: string) =>
              handleDeleteAttachment(attachmentId),
          })}
          dataSource={[
            ...mapDocumentsToDataSource(fields, attachmentDocumentTypeData),
            ...mapAttachmentsToDataSource(licenceAttachmentsData, attachmentDocumentTypeData),
          ]}
        />
        <Accordion
          title={t('legalEntity.licence.additionalInformation.label')}
          name="additionalInformation"
          key="additionalInformation"
          alwaysExpanded
          hideExpandIcon
        />
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="additionalInformation"
            type={EFormInputTypes.textarea}
            minRows={3}
            multiline
            label={t('legalEntity.licence.additionalInformation.label')}
            control={control}
            errors={errors}
            setValue={setValue}
            charsLimit={1000}
            showCounter
          />
        </Grid>

        {!!customFieldsLicence.length && (
          <Accordion
            title={t('legalEntity.licence.customFields.label')}
            name="customFields"
            key="customFields"
            alwaysExpanded
            hideExpandIcon
          >
            <Grid container columnSpacing="20px" sx={{ rowGap: 1 }}>
              {customFieldsLicence.map((customField) => (
                <Grid size={{ xs: 12, lg: 4 }} key={customField.uniqueId}>
                  <FormCustomField
                    data={customField}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </Grid>
              ))}
            </Grid>
          </Accordion>
        )}
        {samePageAlert && (
          <Box sx={{ mb: 2 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        )}
        <Grid container columnSpacing={3} sx={{ marginTop: '30px', rowGap: 3, mt: 2 }}>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel"
              variant={EButtonVariants.outlined}
              onClick={backLink}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="save"
              variant={EButtonVariants.contained}
              disabled={saveButtonIsDisabled}
              isLoading={isSubmitLoading}
            />
          </Grid>

          <Grid sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </div>
    </form>
  );
};

export { LicenceVaryForm };
